import { useState, useCallback } from 'react';
import api, { fmt, fmtQty } from '../../utils/api';

export default function CellcomPage() {
  const [step, setStep] = useState('upload'); // upload | review | confirmed
  const [reviewItems, setReviewItems] = useState([]);
  const [defaultTransport, setDefaultTransport] = useState('SEA');
  const [orderName, setOrderName] = useState('');
  const [confirmedResult, setConfirmedResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const downloadTemplate = async () => {
    try {
      const resp = await api.get('/cellcom/template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = 'Cellcom_Order_Template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { setError('Failed to download template'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/cellcom/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Apply default transport and initialize
      const items = data.items.map(item => ({
        ...item,
        transportMode: defaultTransport,
        allocateQty: Math.min(item.requestedQty, item.availableQty),
        splits: null,
      }));
      setReviewItems(items);
      setStep('review');
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally { setLoading(false); e.target.value = ''; }
  };

  const updateItem = useCallback((idx, updates) => {
    setReviewItems(prev => prev.map((item, i) => i === idx ? { ...item, ...updates } : item));
  }, []);

  const startSplit = (idx) => {
    const item = reviewItems[idx];
    updateItem(idx, {
      splits: [
        { qty: item.allocateQty, transportMode: item.transportMode },
        { qty: 0, transportMode: item.transportMode === 'SEA' ? 'AIR' : 'SEA' },
      ],
    });
  };

  const cancelSplit = (idx) => updateItem(idx, { splits: null });

  const updateSplit = (itemIdx, splitIdx, updates) => {
    setReviewItems(prev => prev.map((item, i) => {
      if (i !== itemIdx) return item;
      const splits = [...item.splits];
      splits[splitIdx] = { ...splits[splitIdx], ...updates };
      return { ...item, splits };
    }));
  };

  const confirmOrder = async () => {
    setLoading(true); setError('');
    try {
      // Build allocation plan: for each item, choose sources FIFO
      const allocationItems = [];

      for (const item of reviewItems) {
        const lines = [];
        const splits = item.splits || [{ qty: item.allocateQty, transportMode: item.transportMode }];

        for (const split of splits) {
          let remaining = split.qty;
          if (remaining <= 0) continue;

          // Allocate from sources FIFO
          for (const src of item.sources) {
            if (remaining <= 0) break;
            const take = Math.min(remaining, src.available);
            if (take > 0) {
              lines.push({ orderNo: src.orderNo, qty: take, transportMode: split.transportMode });
              remaining -= take;
            }
          }
        }

        if (lines.length > 0) {
          allocationItems.push({ sku: item.sku, lines });
        }
      }

      const { data } = await api.post('/cellcom/confirm', {
        items: allocationItems,
        orderName: orderName || undefined,
      });

      setConfirmedResult(data);
      setStep('confirmed');
    } catch (err) {
      setError(err.response?.data?.error || 'Confirmation failed');
    } finally { setLoading(false); }
  };

  const totalRequested = reviewItems.reduce((s, i) => s + i.requestedQty, 0);
  const totalAllocating = reviewItems.reduce((s, i) => {
    if (i.splits) return s + i.splits.reduce((ss, sp) => ss + (sp.qty || 0), 0);
    return s + i.allocateQty;
  }, 0);
  const shortageCount = reviewItems.filter(i => i.shortage > 0).length;

  // ─── UPLOAD STEP ───
  if (step === 'upload') return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <h2 style={{ fontFamily: 'var(--font-mono)', letterSpacing: 2 }}>CELLCOM ORDER</h2>
      <p style={{ color: '#888', marginBottom: 30 }}>Upload a Cellcom order or generate a template for them to fill.</p>

      {error && <div style={{ background: '#fee', color: '#c00', padding: '10px 16px', borderRadius: 8, marginBottom: 20 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 16, marginBottom: 30 }}>
        <button onClick={downloadTemplate} style={{ padding: '12px 24px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
          📥 Download Template
        </button>
      </div>

      <div style={{ background: '#f8f9fa', borderRadius: 12, padding: 30, textAlign: 'center' }}>
        <h3>Upload Cellcom Order</h3>
        <p style={{ color: '#888', marginBottom: 16 }}>Excel file with filled quantities</p>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontWeight: 600, marginInlineEnd: 12 }}>Default Transport:</label>
          {['SEA', 'AIR'].map(t => (
            <button key={t} onClick={() => setDefaultTransport(t)}
              style={{ padding: '6px 18px', marginInlineEnd: 8, borderRadius: 6, border: '1px solid #ccc',
                background: defaultTransport === t ? (t === 'SEA' ? '#1a73e8' : '#e67700') : '#fff',
                color: defaultTransport === t ? '#fff' : '#333', cursor: 'pointer', fontWeight: 600 }}>
              {t === 'SEA' ? '🚢' : '✈️'} {t === 'SEA' ? 'OCEAN' : t}
            </button>
          ))}
        </div>

        <div style={{ marginBottom: 20 }}>
          <input type="text" placeholder="Order name (optional)" value={orderName}
            onChange={e => setOrderName(e.target.value)}
            style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #ddd', width: 260 }} />
        </div>

        <label style={{ display: 'inline-block', padding: '14px 30px', background: '#34a853', color: '#fff',
          borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 16 }}>
          {loading ? 'Processing...' : '📤 Upload Excel File'}
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} style={{ display: 'none' }} disabled={loading} />
        </label>
      </div>
    </div>
  );

  // ─── CONFIRMED STEP ───
  if (step === 'confirmed') return (
    <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
      <h2 style={{ fontFamily: 'var(--font-mono)', letterSpacing: 2, color: '#34a853' }}>✅ ORDER CONFIRMED</h2>
      <div style={{ background: '#f0fdf4', borderRadius: 12, padding: 30, marginTop: 20 }}>
        <p style={{ fontSize: 18, fontWeight: 600 }}>Order ID: {confirmedResult?.orderId}</p>
        <p style={{ fontSize: 24, color: '#34a853', fontWeight: 700 }}>{fmtQty(confirmedResult?.totalAllocated)} units allocated</p>
        <p style={{ color: '#888', marginTop: 10 }}>Warehouse allocations updated. Pick list ready in Warehouse tab.</p>
      </div>
      <button onClick={() => { setStep('upload'); setReviewItems([]); setConfirmedResult(null); }}
        style={{ marginTop: 24, padding: '10px 24px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
        New Order
      </button>
    </div>
  );

  // ─── REVIEW STEP ───
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontFamily: 'var(--font-mono)', letterSpacing: 2, margin: 0 }}>REVIEW ORDER</h2>
        <button onClick={() => { setStep('upload'); setReviewItems([]); }}
          style={{ padding: '6px 16px', background: '#eee', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
          ← Back
        </button>
      </div>

      {error && <div style={{ background: '#fee', color: '#c00', padding: '10px 16px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* Summary boxes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Items</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{reviewItems.length}</div>
        </div>
        <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Requested</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtQty(totalRequested)}</div>
        </div>
        <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Allocating</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#34a853' }}>{fmtQty(totalAllocating)}</div>
        </div>
        <div style={{ background: shortageCount > 0 ? '#fef3f2' : '#f8f9fa', borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Shortages</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: shortageCount > 0 ? '#c00' : '#34a853' }}>{shortageCount}</div>
        </div>
      </div>

      {/* Review table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left' }}>SKU</th>
              <th style={{ padding: '10px 12px', textAlign: 'left' }}>PRODUCT</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>REQUESTED</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>AVAILABLE</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>ALLOCATE</th>
              <th style={{ padding: '10px 12px', textAlign: 'center' }}>TRANSPORT</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>PRICE</th>
              <th style={{ padding: '10px 12px', textAlign: 'center' }}>SPLIT</th>
            </tr>
          </thead>
          <tbody>
            {reviewItems.map((item, idx) => (
              <ReviewRow key={item.sku} item={item} idx={idx}
                updateItem={updateItem} startSplit={startSplit} cancelSplit={cancelSplit} updateSplit={updateSplit} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirm */}
      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <button onClick={confirmOrder} disabled={loading || totalAllocating === 0}
          style={{ padding: '14px 36px', background: totalAllocating > 0 ? '#34a853' : '#ccc', color: '#fff',
            border: 'none', borderRadius: 8, cursor: totalAllocating > 0 ? 'pointer' : 'default',
            fontWeight: 700, fontSize: 16 }}>
          {loading ? 'Processing...' : `✅ Confirm — ${fmtQty(totalAllocating)} units`}
        </button>
      </div>
    </div>
  );
}

function ReviewRow({ item, idx, updateItem, startSplit, cancelSplit, updateSplit }) {
  const hasShortage = item.shortage > 0;
  const price = item.transportMode === 'AIR' ? item.airPrice : item.oceanPrice;

  if (item.splits) {
    return (
      <>
        {item.splits.map((split, si) => (
          <tr key={si} style={{ borderBottom: '1px solid #f0f0f0', background: si === 0 ? '#fff' : '#fafafa' }}>
            {si === 0 && (
              <>
                <td rowSpan={item.splits.length} style={{ padding: '10px 12px', fontWeight: 700 }}>{item.sku}</td>
                <td rowSpan={item.splits.length} style={{ padding: '10px 12px' }}>
                  {item.description}
                  {hasShortage && <span style={{ color: '#c00', fontSize: 11, display: 'block' }}>⚠️ Short {fmtQty(item.shortage)}</span>}
                </td>
                <td rowSpan={item.splits.length} style={{ padding: '10px 12px', textAlign: 'right' }}>{fmtQty(item.requestedQty)}</td>
                <td rowSpan={item.splits.length} style={{ padding: '10px 12px', textAlign: 'right' }}>{fmtQty(item.availableQty)}</td>
              </>
            )}
            <td style={{ padding: '6px 12px', textAlign: 'right' }}>
              <input type="number" value={split.qty} min={0}
                onChange={e => updateSplit(idx, si, { qty: parseFloat(e.target.value) || 0 })}
                style={{ width: 70, padding: '4px 6px', textAlign: 'right', borderRadius: 4, border: '1px solid #ddd' }} />
            </td>
            <td style={{ padding: '6px 12px', textAlign: 'center' }}>
              {['SEA', 'AIR'].map(t => (
                <button key={t} onClick={() => updateSplit(idx, si, { transportMode: t })}
                  style={{ padding: '3px 10px', marginInlineEnd: 4, borderRadius: 4, border: '1px solid #ccc', fontSize: 11,
                    background: split.transportMode === t ? (t === 'SEA' ? '#1a73e8' : '#e67700') : '#fff',
                    color: split.transportMode === t ? '#fff' : '#333', cursor: 'pointer' }}>
                  {t}
                </button>
              ))}
            </td>
            <td style={{ padding: '6px 12px', textAlign: 'right', color: '#34a853', fontWeight: 600 }}>
              {fmt((split.transportMode === 'AIR' ? item.airPrice : item.oceanPrice) * split.qty)}
            </td>
            {si === 0 && (
              <td rowSpan={item.splits.length} style={{ padding: '6px 12px', textAlign: 'center' }}>
                <button onClick={() => cancelSplit(idx)} style={{ padding: '3px 8px', background: '#fee', color: '#c00',
                  border: '1px solid #fcc', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>✕</button>
              </td>
            )}
          </tr>
        ))}
      </>
    );
  }

  return (
    <tr style={{ borderBottom: '1px solid #f0f0f0', background: hasShortage ? '#fef3f2' : '#fff' }}>
      <td style={{ padding: '10px 12px', fontWeight: 700 }}>{item.sku}</td>
      <td style={{ padding: '10px 12px' }}>
        {item.description}
        {hasShortage && <span style={{ color: '#c00', fontSize: 11, display: 'block' }}>⚠️ Short {fmtQty(item.shortage)} units</span>}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right' }}>{fmtQty(item.requestedQty)}</td>
      <td style={{ padding: '10px 12px', textAlign: 'right', color: hasShortage ? '#c00' : 'inherit' }}>{fmtQty(item.availableQty)}</td>
      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
        <input type="number" value={item.allocateQty} min={0} max={item.availableQty}
          onChange={e => updateItem(idx, { allocateQty: Math.min(parseFloat(e.target.value) || 0, item.availableQty) })}
          style={{ width: 70, padding: '4px 6px', textAlign: 'right', borderRadius: 4, border: '1px solid #ddd' }} />
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
        {['SEA', 'AIR'].map(t => (
          <button key={t} onClick={() => updateItem(idx, { transportMode: t })}
            style={{ padding: '3px 10px', marginInlineEnd: 4, borderRadius: 4, border: '1px solid #ccc', fontSize: 11,
              background: item.transportMode === t ? (t === 'SEA' ? '#1a73e8' : '#e67700') : '#fff',
              color: item.transportMode === t ? '#fff' : '#333', cursor: 'pointer' }}>
            {t}
          </button>
        ))}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#34a853', fontWeight: 600 }}>
        {fmt(price * item.allocateQty)}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
        <button onClick={() => startSplit(idx)} title="Split AIR/OCEAN"
          style={{ padding: '3px 8px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 4,
            cursor: 'pointer', fontSize: 11 }}>✂️</button>
      </td>
    </tr>
  );
}
